/**
 * ExcelExportOverlay — user-friendly export progress modal.
 *
 * Shows only what a non-technical user cares about:
 *   - A friendly animated icon + headline
 *   - A smooth progress bar
 *   - How many records are ready so far
 *   - A cancel button while running, Done on success, retry hint on error
 */

import React, { useEffect, useRef, useState } from 'react';
import { Modal, Progress, Button } from 'antd';
import {
  FileExcelOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { runExcelExport } from '../../utils/excelExport';

const fmt = (n) => (n != null ? Number(n).toLocaleString('en-IN') : '—');
const pct = (n) => Math.min(100, Math.max(0, n ?? 0));

// Human-friendly message per engine phase
const PHASE_MESSAGE = {
  init:       'Getting things ready…',
  fetching:   'Collecting your records…',
  writing:    'Building the spreadsheet…',
  finalizing: 'Almost done, putting the finishing touches…',
  done:       'Your file is ready!',
  error:      null,
};

export default function ExcelExportOverlay({ open, exportConfig, onClose, onComplete }) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setProgress(null);
    setError(null);
    setDone(false);

    const controller = new AbortController();
    abortRef.current = controller;

    runExcelExport({
      ...exportConfig,
      signal: controller.signal,
      onProgress: (p) => setProgress(p),
    })
      .then((metrics) => {
        setDone(true);
        onComplete?.(metrics);
        setTimeout(() => onClose?.(), 2000);
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          onClose?.();
        } else {
          setError(err?.message || 'Something went wrong. Please try again.');
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const phase = done ? 'done' : error ? 'error' : (progress?.phase ?? 'init');
  const percentage = pct(progress?.percentage);
  const isRunning = !done && !error;

  const progressColor =
    phase === 'done'       ? '#21B546' :
    phase === 'error'      ? '#ef4444' :
    phase === 'finalizing' ? '#1A9B3A' :
                             '#21B546';

  const exported   = progress?.exportedCount ?? 0;
  const total      = progress?.totalRecords  ?? 0;
  const hasTotal   = total > 0;

  return (
    <Modal
      open={open}
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      width={420}
      styles={{ body: { padding: '36px 32px 28px' } }}
    >
      {/* ── Icon ── */}
      <div className="mb-5 flex justify-center">
        {phase === 'done' ? (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
            <CheckCircleFilled className="text-3xl text-green-500" />
          </div>
        ) : phase === 'error' ? (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <CloseCircleFilled className="text-3xl text-red-400" />
          </div>
        ) : (
          <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary-muted">
            <FileExcelOutlined className="text-2xl text-primary" />
            <span className="absolute -right-0.5 -top-0.5">
              <LoadingOutlined className="text-sm text-primary" spin />
            </span>
          </div>
        )}
      </div>

      {/* ── Headline + subtitle ── */}
      <div className="mb-6 text-center">
        <h3 className="text-base font-semibold text-gray-800">
          {phase === 'done'  ? 'Export Complete'  :
           phase === 'error' ? 'Export Failed'     :
                               'Exporting to Excel'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {phase === 'error'
            ? 'Something went wrong. Please try again.'
            : PHASE_MESSAGE[phase]}
        </p>
      </div>

      {/* ── Progress bar ── */}
      {!error && (
        <div className="mb-6">
          <Progress
            percent={percentage}
            strokeColor={progressColor}
            trailColor="#e5e7eb"
            size={['100%', 8]}
            showInfo={false}
            status={done ? 'success' : 'active'}
            strokeLinecap="round"
          />

          {/* Records counter — the only number a user cares about */}
          <div className="mt-3 text-center">
            {done ? (
              <p className="text-sm font-medium text-gray-700">
                {fmt(exported)} record{exported !== 1 ? 's' : ''} exported successfully
              </p>
            ) : hasTotal ? (
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-primary">{fmt(exported)}</span>
                {' '}of{' '}
                <span className="font-semibold text-gray-700">{fmt(total)}</span>
                {' '}records ready
              </p>
            ) : exported > 0 ? (
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-primary">{fmt(exported)}</span>
                {' '}records collected so far…
              </p>
            ) : (
              <p className="text-sm text-gray-400">Starting…</p>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex justify-center">
        {done && (
          <Button type="primary" size="large" className="min-w-[120px]" onClick={onClose}>
            Done
          </Button>
        )}
        {error && (
          <Button type="primary" danger size="large" onClick={onClose}>
            Close
          </Button>
        )}
        {isRunning && (
          <button
            type="button"
            onClick={() => abortRef.current?.abort()}
            className="text-sm text-gray-400 transition-colors hover:text-gray-600"
          >
            Cancel export
          </button>
        )}
      </div>

      {/* ── Finalising notice ── */}
      {phase === 'finalizing' && (
        <p className="mt-4 text-center text-xs text-gray-400">
          Please keep this tab open while the file is being prepared.
        </p>
      )}
    </Modal>
  );
}
